import { useMemo } from 'react'
import type { ComponentType } from 'react'
import { GearSixIcon } from '@phosphor-icons/react'
import { useVisibleNavItems } from './useVisibleNavItems'
import {
  ADMIN_SETTINGS_SECTIONS,
  MODULE_SETTINGS_SECTIONS,
  USER_SETTINGS_TABS,
} from '@/config/settings-sections'
import { MODULE_SETTINGS_PATHS } from '@/config/settings-directory'

export interface OmniNavTarget {
  id: string
  label: string
  /** Org-relative path — pass through getOrgPath() before navigating */
  path: string
  icon: ComponentType<{ className?: string }>
  group: 'module' | 'settings'
  keywords: string[]
}

// Extra search aliases per module href so "vendors" finds Suppliers, etc.
const MODULE_KEYWORDS: Record<string, string[]> = {
  '/today': ['overview', 'home', 'dashboard'],
  '/tasks/mine': ['my work', 'my day', 'tasks'],
  '/notes/mine': ['notes'],
  '/my-time': ['time tracking', 'timer', 'hours'],
  '/calendar': ['schedule', 'planner'],
  '/my-organization': ['home base', 'organization', 'org'],
  '/projects': ['folders'],
  '/tasks': ['todo', 'work'],
  '/tickets': ['helpdesk', 'support'],
  '/knowledge-base': ['kb', 'articles', 'wiki', 'help'],
  '/collections': ['database', 'lists'],
  '/forms': ['surveys'],
  '/threads': ['discussions', 'chat'],
  '/documents': ['files', 'docs'],
  '/events': ['calendar'],
  '/contacts': ['crm', 'people', 'address book', 'leads'],
  '/deals': ['pipeline', 'sales', 'crm'],
  '/marketing': ['campaigns', 'email', 'journeys'],
  '/scheduling': ['booking', 'appointments'],
  '/live-chat': ['chat', 'widget'],
  '/quotes': ['proposals', 'estimates'],
  '/products': ['price book', 'catalog'],
  '/communications': ['calls', 'calling', 'phone', 'sequences'],
  '/entities': ['clients', 'companies', 'accounts'],
  '/suppliers': ['vendors'],
  '/team': ['members', 'staff', 'people'],
  '/timesheets': ['time approval', 'hours'],
  '/workflows': ['automation'],
  '/contracts': ['agreements'],
  '/invoices': ['billing'],
  '/expenses': ['receipts', 'spend'],
}

const ADMIN_LABELS: Record<string, string> = {
  '/admin/members': 'Organization Members',
  '/admin/integrations': 'Integrations',
  '/admin/settings': 'Organization Settings',
  '/admin/logs': 'Audit Trail',
  '/admin/archive': 'Archive',
  '/admin/subscription': 'Subscription & Billing',
}

const ADMIN_KEYWORDS: Record<string, string[]> = {
  '/admin/members': ['admin', 'users', 'invite', 'roles', 'permissions'],
  '/admin/integrations': ['admin', 'connect', 'slack', 'github', 'quickbooks', 'outlook'],
  '/admin/settings': ['admin', 'general', 'workspace', 'organization', 'finance'],
  '/admin/logs': ['admin', 'audit', 'log', 'history', 'compliance', 'who changed', 'access'],
  '/admin/archive': ['admin', 'archive', 'archived', 'deleted', 'restore', 'recover', 'undelete', 'recycle bin', 'trash'],
  '/admin/subscription': ['admin', 'billing', 'plan', 'payment', 'upgrade', 'seats'],
}

/**
 * Navigation destinations (modules + settings pages) searchable from
 * OmniSearch. Applies the same visibility gating as AppSidebar
 * (accountTypes → featureFlag, else subscriptionFeature → permission), but
 * includes `hidden` nav items — search is exactly where non-sidebar
 * destinations should stay discoverable.
 */
export function useOmniNavTargets(): OmniNavTarget[] {
  const {
    visibleMainNavItems,
    adminNavItems,
    userMenuItems,
    isAdminOrOwner,
    isPersonal,
    accountType,
  } = useVisibleNavItems()

  return useMemo(() => {
    const visibleModules = visibleMainNavItems

    const moduleTargets: OmniNavTarget[] = visibleModules.map(item => ({
      id: `module:${item.href}`,
      label: item.label,
      path: item.href,
      icon: item.icon,
      group: 'module',
      keywords: MODULE_KEYWORDS[item.href] ?? [],
    }))

    const settingsTargets: OmniNavTarget[] = []

    // The unified settings hub — the directory of everything below.
    settingsTargets.push({
      id: 'settings:/settings',
      label: 'Workspace Settings',
      path: '/settings',
      icon: GearSixIcon,
      group: 'settings',
      keywords: [
        'settings',
        'preferences',
        'hub',
        'all settings',
        'workspace',
        'organization',
      ],
    })

    // Per-module settings pages, gated by the module's own visibility.
    // Section entries deep-link via ?section= (pages resolve it through
    // useSettingsSection or their own param handling).
    for (const item of visibleModules) {
      const settingsPath = MODULE_SETTINGS_PATHS[item.href]
      if (!settingsPath) continue
      settingsTargets.push({
        id: `settings:${settingsPath}`,
        label: `${item.label} Settings`,
        path: settingsPath,
        icon: item.icon,
        group: 'settings',
        keywords: [
          'settings',
          'preferences',
          'configure',
          ...(MODULE_KEYWORDS[item.href] ?? []),
        ],
      })

      const sectionConfig = MODULE_SETTINGS_SECTIONS[item.href]
      if (!sectionConfig) continue
      const param = sectionConfig.param ?? 'section'
      for (const section of sectionConfig.sections) {
        settingsTargets.push({
          id: `settings:${settingsPath}:${section.id}`,
          label: `${item.label} · ${section.label}`,
          path: `${settingsPath}?${param}=${section.id}`,
          icon: item.icon,
          group: 'settings',
          // No generic 'settings' keyword here — a bare "settings" query
          // should surface pages, not flood with every section.
          keywords: [
            ...(section.keywords ?? []),
            ...(MODULE_KEYWORDS[item.href] ?? []),
          ],
        })
      }
    }

    for (const item of userMenuItems) {
      settingsTargets.push({
        id: `settings:${item.href}`,
        label: item.label,
        path: item.href,
        icon: item.icon,
        group: 'settings',
        keywords: [
          'settings',
          'account',
          'profile',
          'preferences',
          'integrations',
          'skills',
          'email',
          'timezone',
        ],
      })
      if (item.href === '/user-settings') {
        for (const tab of USER_SETTINGS_TABS) {
          settingsTargets.push({
            id: `settings:/user-settings:${tab.id}`,
            label: `${item.label} · ${tab.label}`,
            path: `/user-settings#${tab.id}`,
            icon: item.icon,
            group: 'settings',
            keywords: tab.keywords ?? [],
          })
        }
      }
    }

    if (isAdminOrOwner) {
      for (const item of adminNavItems) {
        if (item.accountTypes && !item.accountTypes.includes(accountType)) {
          continue
        }
        settingsTargets.push({
          id: `settings:${item.href}`,
          label: ADMIN_LABELS[item.href] ?? item.label,
          path: item.href,
          icon: item.icon,
          group: 'settings',
          keywords: ['settings', ...(ADMIN_KEYWORDS[item.href] ?? [])],
        })
        if (item.href === '/admin/settings') {
          for (const section of ADMIN_SETTINGS_SECTIONS) {
            if (section.businessOnly && isPersonal) continue
            if (section.personalOnly && !isPersonal) continue
            settingsTargets.push({
              id: `settings:/admin/settings:${section.id}`,
              label: `Organization Settings · ${section.label}`,
              path: `/admin/settings?section=${section.id}`,
              icon: item.icon,
              group: 'settings',
              keywords: section.keywords ?? [],
            })
          }
        }
      }
    }

    return [...moduleTargets, ...settingsTargets]
  }, [
    accountType,
    adminNavItems,
    isAdminOrOwner,
    isPersonal,
    userMenuItems,
    visibleMainNavItems,
  ])
}

/**
 * Match nav targets against a search query. Ranked: label prefix, word
 * prefix, label substring, keyword prefix, keyword substring. Single-char
 * queries only match label prefixes to keep noise down.
 */
export function matchNavTargets(
  targets: OmniNavTarget[],
  query: string,
): OmniNavTarget[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const scored: { target: OmniNavTarget; score: number }[] = []

  for (const target of targets) {
    const label = target.label.toLowerCase()
    let score: number | null = null

    if (label.startsWith(q)) {
      score = 0
    } else if (q.length >= 2) {
      if (label.split(/\s+/).some(word => word.startsWith(q))) {
        score = 1
      } else if (label.includes(q)) {
        score = 2
      } else if (target.keywords.some(k => k.startsWith(q))) {
        score = 3
      } else if (target.keywords.some(k => k.includes(q))) {
        score = 4
      } else {
        // Multi-word queries: every token must appear somewhere.
        const tokens = q.split(/\s+/)
        if (tokens.length > 1) {
          const haystack = `${label} ${target.keywords.join(' ')}`
          if (tokens.every(token => haystack.includes(token))) {
            score = 5
          }
        }
      }
    }

    if (score !== null) {
      scored.push({ target, score })
    }
  }

  return scored
    .sort(
      (a, b) =>
        a.score - b.score || a.target.label.length - b.target.label.length,
    )
    .map(entry => entry.target)
}
